/**
 * ドラッグ＆ドロップとファイル選択の受け口。
 *
 * 【iOS について】
 * `accept` に image/* を含めると、iOS Safari のファイル選択で
 * 「フォトライブラリ」が選べるようになる。動画は端末側で弾く
 * （CLAUDE.md 原則4。判定は uploadQueue.ts の rejectionReason が持つ）。
 *
 * 【ドラッグ判定について】
 * dragenter / dragleave は子要素をまたぐたびに発火するため、
 * 素直に真偽値で持つと枠が点滅する。深さを数えて打ち消す。
 */
import { useRef, useState, type ReactNode } from 'react'
import { filesFromDataTransfer } from '../lib/uploadQueue.ts'

export function DropZone({
  onFiles,
  disabled,
  allowVideo,
  children,
}: {
  onFiles: (files: File[]) => void
  disabled?: boolean
  /** 動画を選べるか（モバイルでは false にして写真だけにする）。 */
  allowVideo: boolean
  children?: ReactNode
}) {
  const [dragging, setDragging] = useState(false)
  const depth = useRef(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const accept = allowVideo ? 'video/*,image/*' : 'image/*'

  const reset = () => {
    depth.current = 0
    setDragging(false)
  }

  return (
    <div
      className={`dropzone${dragging ? ' over' : ''}${disabled ? ' disabled' : ''}`}
      onDragEnter={(e) => {
        e.preventDefault()
        if (disabled) return
        depth.current += 1
        setDragging(true)
      }}
      onDragOver={(e) => {
        // これを止めないとブラウザがファイルを開いてしまう
        e.preventDefault()
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        depth.current -= 1
        if (depth.current <= 0) reset()
      }}
      onDrop={(e) => {
        e.preventDefault()
        reset()
        if (disabled) return
        const files = filesFromDataTransfer(e.dataTransfer)
        if (files.length > 0) onFiles(files)
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length > 0) onFiles(files)
          // 同じファイルを選び直せるように空にする
          e.target.value = ''
        }}
      />
      <div className="dropzone-inner">
        <p className="dropzone-lead">
          {allowVideo
            ? 'ここにファイルをドラッグ＆ドロップ'
            : '写真を選んでアップロード'}
        </p>
        <button
          type="button"
          className="primary"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          {allowVideo ? 'ファイルを選ぶ' : '写真ライブラリから選ぶ'}
        </button>
        <p className="muted small">
          {allowVideo
            ? '動画・写真とも複数まとめて選べます。動画は1ファイル50GBまで。'
            : 'この端末からは写真のみ送れます。動画はPCブラウザからお願いします。'}
        </p>
        {children}
      </div>
    </div>
  )
}
